'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import EnrollmentForm from '@/components/EnrollmentForm';
import EnrollmentModal from '@/components/EnrollmentModal';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroSlides = [
    {
      title: 'Transform Potential into Expertise',
      subtitle: 'Master in-demand skills through project-based learning with industry mentors',
      cta: 'Start Your Journey',
      highlight: '90% Job Placement Rate',
    },
    {
      title: 'Learn by Building, Not Just Watching',
      subtitle: 'Hands-on projects that mirror real-world challenges and industry standards',
      cta: 'Explore Programs',
      highlight: 'Average 45% Salary Increase',
    },
    {
      title: 'Your Career Breakthrough Starts Here',
      subtitle: 'Join Hundreds of professionals who transformed their careers with VitiQuest',
      cta: 'View Success Stories',
      highlight: '250+ Alumni Network',
    },
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, heroSlides.length]);

  const handleSlideChange = (index: number) => {
    if (!isHydrated) return;
    setCurrentSlide(index);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Clear error for this field when user starts typing
  };

  const validateForm = (): boolean => {
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <section className={`relative bg-gradient-to-br from-sky-light/20 via-background to-primary/10 overflow-hidden ${className}`}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-light/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Side */}
          <div className="space-y-8 animate-ascend">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-card px-4 py-2 rounded-full shadow-sky-sm border border-primary/20">
              <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-primary" />
              <span className="text-sm font-body font-medium text-foreground">
                {heroSlides[currentSlide].highlight}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold text-foreground leading-tight">
              {heroSlides[currentSlide].title}
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-muted-foreground font-body leading-relaxed max-w-xl">
              {heroSlides[currentSlide].subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/programs-overview"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-base shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105"
              >
                {heroSlides[currentSlide].cta}
                <Icon name="ArrowRightIcon" size={20} variant="outline" className="ml-2" />
              </Link>
              <Link
                href="/about"
                className="inline-flex items-center justify-center px-8 py-4 bg-card text-foreground border-2 border-primary/20 rounded-lg font-headline font-semibold text-base hover:bg-primary/5 transition-all duration-300"
              >
                Learn More
                <Icon name="InformationCircleIcon" size={20} variant="outline" className="ml-2" />
              </Link>
            </div>

            {/* Slide Indicators */}
            <div className="flex items-center space-x-3 pt-4">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleSlideChange(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index ? 'w-8 bg-primary' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Visual Side */}
          <div className="relative lg:h-[600px] animate-ascend" style={{ animationDelay: '0.2s' }}>
            <div className="relative h-full bg-card rounded-2xl shadow-sky-lg p-8 border border-primary/10">
              {/* Enrollment Form */}
              <EnrollmentForm onClose={() => setIsModalOpen(false)} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
